import React, { JSX, useEffect, useState } from 'react';

import * as Yup from 'yup';

import { useNavigate } from 'react-router-dom';

import { Button, Col, Form, Input, Row, Typography } from 'antd';

import './Loginpage.scss';

import { BackendAPI } from '../axios';

import axios from 'axios';

import Swal from 'sweetalert2';

import withReactContent from 'sweetalert2-react-content';

import {
    EyeInvisibleOutlined,
    EyeOutlined,
    LockOutlined,
    ReloadOutlined,
    UserOutlined,
    SoundOutlined
} from '@ant-design/icons';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { ToastContainer } from 'react-toastify';

const { Link } = Typography;

const MySwal = withReactContent(Swal);

interface PageSettingsProps {
    setLoginmodel: (value: boolean) => void;
    setForgetModal: (value: boolean) => void;
    setMessege: (value: boolean) => void;
}

function LoginPage({
    setLoginmodel,
    setForgetModal
}: PageSettingsProps): JSX.Element {

    const [formTouched, setFormTouched] = useState<{ [key: string]: boolean }>({});
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [captchaImage, setCaptchaImage] = useState('');
    const [captchaText, setCaptchaText] = useState('');
    const [error, setError] = useState<string | { [key: string]: string }>('');

    // Username stays read-only until focused so browsers don't autofill it
    const [usernameReadOnly, setUsernameReadOnly] = useState(true);

    const labelStyle = {
        fontWeight: "bold",
        fontSize: '16px',
    };

    const validationSchema = Yup.object().shape({
        username: Yup.string()
            .required('Username is required'),

        password: Yup.string()
            .required('Password is required')
            .min(8, 'Password should be at least 8 characters long')
            .matches(
                /^(?=.*[A-Za-z])(?=.*\d).+$/,
                'Password should contain both letters and numbers for better security'
            ),

        captcha: Yup.string()
            .required('Captcha is required')
            .test(
                'captcha-matches',
                'Incorrect captcha',
                (value) => value === captchaText
            ),
    });

    const {
        control,
        handleSubmit,
        formState: {
            errors,
            touchedFields,
            isSubmitted
        }
    } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onTouched',
    });

    const navigate = useNavigate();

    const onSubmit = async (values: any) => {

        const username = values.username;
        const password = values.password;
        const captcha = values.captcha;

        try {

            const loginData = {
                username,
                password,
                captcha
            };

            const header = {
                headers: {
                    "Content-Type": "application/json",
                },
                withCredentials: true,
            };

            const url = `/user/login`;

            const response = await BackendAPI.post(
                url,
                loginData,
                header
            );

            if (response.status === 200) {

                localStorage.setItem(
                    'authToken',
                    response.data.token
                );

                localStorage.setItem(
                    'initialTime',
                    response.data.expiresAt
                );

                const INITIAL_TIME =
                    localStorage.getItem('initialTime');

                const remainingSeconds =
                    INITIAL_TIME
                        ? Number(INITIAL_TIME) / 1000
                        : 0;

                localStorage.setItem(
                    "remainingTime",
                    remainingSeconds.toString()
                );

                localStorage.setItem(
                    "lastSavedTime",
                    Date.now().toString()
                );

                navigate('/user/homepage');

            }

        } catch (error: any) {

            let message = '';

            try {
                message = JSON.parse(
                    error.request.response
                ).message;
            } catch {
                message = '';
            }

            const newErrors: { [key: string]: string } = {};

            if (error.inner && error.inner.length > 0) {

                error.inner.forEach((error: any) => {

                    if (error.path) {
                        newErrors[error.path] = error.message;
                    }

                });
            }

            setError(newErrors);

            setFormTouched({
                username: true,
                password: true,
                captcha: true
            });

            if (axios.isAxiosError(error)) {

                if (error.request.status === 401) {

                    MySwal.fire({
                        icon: 'error',
                        imageWidth: 100,
                        imageHeight: 100,
                        title: 'Invalid Credentials',
                        text: 'You have entered wrong credentials',
                    });

                } else if (message === 'User not found') {

                    MySwal.fire({
                        icon: 'error',
                        title: 'User not found',
                        text: 'Please register first.',
                        imageWidth: 100,
                        imageHeight: 100,
                    });

                } else if (error.request.status === 423) {

                    const serverResponse =
                        error.response?.data.error;

                    const lockTime: string =
                        serverResponse.match(/until (.*)/)[1];

                    const lockDate: Date =
                        new Date(lockTime);

                    const now: Date =
                        new Date();

                    const remainingTime: number =
                        lockDate.getTime() - now.getTime();

                    const remainingSeconds: number =
                        Math.floor(remainingTime / 1000);

                    const minutes: number =
                        Math.floor(remainingSeconds / 60);

                    const seconds: number =
                        remainingSeconds % 60;

                    MySwal.fire({
                        icon: 'error',
                        title: 'Account Locked',
                        html: `
                            Your account is locked.
                            Please try again in
                            <span style="color: #FF5733; font-weight: bold;">
                                ${minutes} minute(s)
                            </span>
                            and
                            <span style="color: #33B5FF; font-weight: bold;">
                                ${seconds} second(s)
                            </span>.
                        `,
                        imageWidth: 100,
                        imageHeight: 100,
                    });

                } else {

                    MySwal.fire({
                        icon: 'error',
                        title: 'Something went wrong',
                        text: 'Please try again later.',
                        imageWidth: 100,
                        imageHeight: 100,
                    });
                }
            }

            setError(
                'Authentication error. Please try again.'
            );
        }
    };

    useEffect(() => {
        fetchCaptchaImage();
    }, []);

    const fetchCaptchaImage = async () => {

        const header = {
            headers: {
                "Content-Type": "application/json",
            },
            withCredentials: true,
        };

        const url = `/user/generate`;

        try {

            const response =
                await BackendAPI.get(url, header);

            setCaptchaImage(
                response.data.base64Image
            );

            setCaptchaText(
                response.data.captchaText
            );

        } catch {
            // Captcha could not be loaded; user can press refresh to retry
        }
    };

    const handleOpen = () => {

        setLoginmodel(false);
        setForgetModal(true);
    };

    const playCaptchaAudio = () => {

        if (!captchaText) return;

        window.speechSynthesis.cancel();

        const spokenText = captchaText
            .split('')
            .map(char => {

                if (/[a-z]/.test(char))
                    return `lowercase ${char}`;

                if (/[A-Z]/.test(char))
                    return `uppercase ${char}`;

                if (/[0-9]/.test(char))
                    return `digit ${char}`;

                return char;

            })
            .join(', ');

        const utterance =
            new SpeechSynthesisUtterance(
                spokenText
            );

        utterance.lang = 'en-US';
        utterance.rate = 0.6;
        utterance.pitch = 1;
        utterance.volume = 1;

        window.speechSynthesis.speak(
            utterance
        );
    };

    return (
        <>
            <ToastContainer />

            {/*
              * The password is a masked text input (not type="password"),
              * so the browser doesn't treat this as a login form and
              * doesn't show saved-credential suggestions.
              */}
            <style>{`
                .pwd-mask input {
                    -webkit-text-security: disc;
                    text-security: disc;
                }
            `}</style>

            <Form
                className='login-form-modal'
                layout="vertical"
                onFinish={handleSubmit(onSubmit)}
                autoComplete="off"
            >

                <div
                    className='login-head'
                    style={{
                        textAlign: 'center',
                        marginBottom: '20px'
                    }}
                >

                    <UserOutlined
                        style={{
                            padding: '10px',
                            background: '#daecff',
                            borderRadius: '30px',
                            position: 'static',
                            fontSize: '40px'
                        }}
                    />

                    <h2 style={{ margin: '5px' }}>
                        AUA/Sub-AUA Login
                    </h2>

                </div>

                <Col xs={24} md={24}>

                    <Form.Item
                        label={
                            <span style={labelStyle}>
                                Username
                            </span>
                        }

                        validateStatus={
                            errors.username
                                ? 'error'
                                : ''
                        }

                        help={
                            (touchedFields.username ||
                                isSubmitted) &&
                            errors.username
                                ? errors.username.message
                                : ''
                        }
                    >

                        <Controller
                            name="username"
                            control={control}

                            render={({ field }) => (

                                <Input
                                    prefix={
                                        <UserOutlined
                                            style={{
                                                fontSize: '18px'
                                            }}
                                        />
                                    }

                                    {...field}

                                    style={{
                                        height: '40px'
                                    }}

                                    className="custom-placeholder"
                                    placeholder='Enter Username'
                                    autoComplete="off"
                                    readOnly={usernameReadOnly}
                                    onFocus={() => setUsernameReadOnly(false)}
                                />

                            )}
                        />

                    </Form.Item>

                </Col>

                <Row>

                    <Col xs={24}>

                        <Form.Item
                            label={
                                <span style={labelStyle}>
                                    Password
                                </span>
                            }

                            validateStatus={
                                errors.password
                                    ? 'error'
                                    : ''
                            }

                            help={
                                (touchedFields.password ||
                                    isSubmitted) &&
                                errors.password
                                    ? errors.password.message
                                    : ''
                            }

                            style={{
                                marginBottom: 4
                            }}
                        >

                            <Controller
                                name="password"
                                control={control}

                                render={({ field }) => (

                                    <Input
                                        type="text"

                                        prefix={
                                            <LockOutlined
                                                style={{
                                                    fontSize: '18px'
                                                }}
                                            />
                                        }

                                        {...field}

                                        style={{
                                            height: '40px'
                                        }}

                                        className={
                                            showLoginPassword
                                                ? 'custom-placeholder'
                                                : 'custom-placeholder pwd-mask'
                                        }

                                        placeholder='Enter Password'

                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                        data-lpignore="true"
                                        data-1p-ignore="true"

                                        suffix={
                                            <span
                                                onClick={() =>
                                                    setShowLoginPassword(
                                                        (s) => !s
                                                    )
                                                }
                                                style={{
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {showLoginPassword
                                                    ? <EyeOutlined />
                                                    : <EyeInvisibleOutlined />}
                                            </span>
                                        }
                                    />

                                )}
                            />

                        </Form.Item>

                    </Col>

                    <Col
                        xs={24}
                        style={{
                            textAlign: 'right',
                            marginBottom: 12
                        }}
                    >

                        <Link
                            onClick={handleOpen}
                            style={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: '#0056b3',
                                cursor: 'pointer'
                            }}
                        >
                            Forgot Password?
                        </Link>

                    </Col>

                </Row>

                <div
                    style={{
                        marginBottom: 8,
                        display: 'flex',
                        gap: '6px'
                    }}
                >

                    <div
                        style={{
                            color: "#fff",
                            width: '100%',
                            borderRadius: '10px',
                        }}
                    >

                        <img
                            src={`data:image/png;base64,${captchaImage}`}
                            alt="CAPTCHA"

                            style={{
                                width: '100%',
                                height: '50px',
                                borderRadius: '10px',
                                border:
                                    '2px solid #1515b4'
                            }}
                        />

                    </div>

                    <Button
                        type="link"
                        onClick={fetchCaptchaImage}

                        style={{
                            background:
                                'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontSize: '24px',
                            height: '50px',
                            color: 'white',
                            width: '12%'
                        }}
                    >
                        <ReloadOutlined />
                    </Button>

                    <Button
                        type="link"
                        onClick={playCaptchaAudio}

                        style={{
                            background:
                                'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontSize: '24px',
                            height: '50px',
                            color: 'white',
                            width: '12%'
                        }}
                    >
                        <SoundOutlined />
                    </Button>

                </div>

                <Row gutter={[24, 8]}>

                    <Col
                        xs={24}
                        md={24}
                        lg={24}
                    >

                        <Row>

                            <Col
                                xs={24}
                                sm={24}
                                md={24}
                                lg={24}
                            >

                                <Form.Item
                                    validateStatus={
                                        errors.captcha
                                            ? 'error'
                                            : ''
                                    }

                                    help={
                                        (touchedFields.captcha ||
                                            isSubmitted) &&
                                        errors.captcha
                                            ? errors.captcha.message
                                            : ''
                                    }
                                >

                                    <Controller
                                        name="captcha"
                                        control={control}

                                        render={({ field }) => (

                                            <Input
                                                {...field}

                                                style={{
                                                    height: '40px'
                                                }}

                                                className="custom-placeholder"

                                                placeholder='Enter Captcha'

                                                autoComplete="off"
                                            />

                                        )}
                                    />

                                </Form.Item>

                            </Col>

                        </Row>

                    </Col>

                    <Col
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}

                        style={{
                            textAlign: 'center'
                        }}
                    >

                        <Button
                            type="primary"
                            htmlType="submit"

                            style={{
                                width: '100%',
                                background:
                                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                boxShadow:
                                    '0 4px 12px rgba(102, 126, 234, 0.3)',
                                transition:
                                    'all 0.3s ease',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 30px',
                                fontSize: '16px',
                                fontWeight: '600',
                            }}

                            onMouseEnter={(e) => {

                                (
                                    e.target as HTMLButtonElement
                                ).style.boxShadow =
                                    '0 6px 20px rgba(102, 126, 234, 0.5)';

                            }}

                            onMouseLeave={(e) => {

                                (
                                    e.target as HTMLButtonElement
                                ).style.boxShadow =
                                    '0 4px 12px rgba(102, 126, 234, 0.3)';

                            }}
                        >
                            Submit
                        </Button>

                    </Col>

                </Row>

            </Form>
        </>
    );
}

export default LoginPage;
